import React, { useState, useEffect } from 'react';
import { NodeProperty } from '@shared/types/node-properties';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, Loader2 } from 'lucide-react';
import { knowledgeBaseApi, nodesApi } from '@/lib/api';
import { useWorkflowStore } from '@/stores/workflowStore';

interface OptionsPropertyRendererProps {
  property: NodeProperty;
  value: string | number;
  onChange: (value: string | number) => void;
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
}

export const OptionsPropertyRenderer: React.FC<OptionsPropertyRendererProps> = ({
  property,
  value,
  onChange,
  disabled = false,
  error,
  hideLabel = false,
}) => {
  const [dynamicOptions, setDynamicOptions] = useState<{ value: string | number; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get workflow context to filter collections based on workflow location
  const currentWorkflow = useWorkflowStore((state) => state.currentWorkflow);

  // Get optionsSource from property
  const optionsSource = (property as any).optionsSource;

  // Fetch dynamic options when optionsSource is specified
  useEffect(() => {
    if (!optionsSource) return;

    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        if (optionsSource === 'knowledgeBaseCollections') {
          // Use filtered collections based on workflow location type
          // workflowLocationType: 0 = Personal, 1 = Team
          const isTeamWorkflow = currentWorkflow?.workflowLocationType === 1;
          const locationType: 'personal' | 'team' = isTeamWorkflow ? 'team' : 'personal';
          const teamFolderId = isTeamWorkflow ? currentWorkflow?.teamFolderId ?? undefined : undefined;

          const response = await knowledgeBaseApi.getCollectionsForWorkflow(locationType, teamFolderId ?? undefined);
          const collections = response.data?.collections || response.data || [];

          setDynamicOptions(
            collections.map((col: { id: string; name: string; visibility?: string }) => ({
              value: col.id,
              name: col.name,
            }))
          );
        } else if (optionsSource === 'litellmModels' || optionsSource === 'litellmEmbeddingModels') {
          // Fetch available models from LiteLLM proxy (uses system credential)
          const endpoint = optionsSource === 'litellmEmbeddingModels' ? 'getLiteLLMEmbeddingModels' : 'getLiteLLMModels';
          const response = await (nodesApi as any)[endpoint]();
          const result = response.data;

          if (result?.success && result.options) {
            setDynamicOptions(
              result.options.map((opt: { value: string; label: string; description?: string }) => ({
                value: opt.value,
                name: opt.label,
                description: opt.description,
              }))
            );
          } else {
            console.error('Failed to fetch LiteLLM models:', result?.error);
            setDynamicOptions([]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch dynamic options:', err);
        setDynamicOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [optionsSource, currentWorkflow?.workflowLocationType, currentWorkflow?.teamFolderId]);

  // Use dynamic options if optionsSource is specified, otherwise use static options
  const options = optionsSource ? dynamicOptions : (Array.isArray(property.options) ? property.options : []);
  const displayStyle = property.typeOptions?.displayStyle || 'dropdown';

  const renderDropdown = () => (
    <Select
      value={String(value || '')}
      onValueChange={(val) => {
        // Try to preserve number type if original was number
        const option = options.find((opt: any) => String(opt.value) === val);
        const optValue = option?.value;
        if (optValue !== undefined && optValue !== null) {
          onChange(typeof optValue === 'boolean' ? (optValue ? 1 : 0) : optValue);
        } else {
          onChange(val);
        }
      }}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className={error ? 'border-destructive' : ''}>
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        ) : (
          <SelectValue placeholder={property.placeholder || 'Select an option'} />
        )}
      </SelectTrigger>
      <SelectContent>
        {options.map((option: any) => (
          <SelectItem key={String(option.value)} value={String(option.value)}>
            <div className="flex flex-col">
              <span>{option.name}</span>
              {option.description && (
                <span className="text-xs text-muted-foreground">{option.description}</span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderRadio = () => (
    <RadioGroup
      value={String(value || '')}
      onValueChange={(val) => {
        const option = options.find((opt: any) => String(opt.value) === val);
        const optValue = option?.value;
        if (optValue !== undefined && optValue !== null) {
          onChange(typeof optValue === 'boolean' ? (optValue ? 1 : 0) : optValue);
        } else {
          onChange(val);
        }
      }}
      className="space-y-2"
    >
      {options.map((option: any) => (
        <div key={String(option.value)} className="flex items-start space-x-2">
          <RadioGroupItem value={String(option.value)} id={`${property.key}-${option.value}`} />
          <div className="flex flex-col">
            <Label
              htmlFor={`${property.key}-${option.value}`}
              className="font-normal cursor-pointer"
            >
              {option.name}
            </Label>
            {option.description && (
              <span className="text-xs text-muted-foreground">{option.description}</span>
            )}
          </div>
        </div>
      ))}
    </RadioGroup>
  );

  // If hideLabel is true, just render the control without label/description
  if (hideLabel) {
    return (
      <>
        {displayStyle === 'radio' ? renderRadio() : renderDropdown()}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Label>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{property.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {displayStyle === 'radio' ? renderRadio() : renderDropdown()}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {property.hint && !error && (
        <p className="text-xs text-muted-foreground">{property.hint}</p>
      )}
    </div>
  );
};
